import { InitPageStrict } from "../index";
import * as Toastr from "toastr";

export interface KeonnCalibration {
  originX: number;
  originY: number;
  mmPerPx: number;
  swapXY: boolean;
  flipX: boolean;
  flipY: boolean;
}

export function isCalibrated(c: KeonnCalibration): boolean {
  return Number.isFinite(c.originX) && Number.isFinite(c.originY) && c.mmPerPx > 0;
}

export function mmToPx(c: KeonnCalibration, x: number, y: number): [number, number] | null {
  if (!isCalibrated(c)) return null;
  let u = x / c.mmPerPx;
  let v = y / c.mmPerPx;
  if (c.swapXY) [u, v] = [v, u];
  if (c.flipX) u = -u;
  if (c.flipY) v = -v;
  return [c.originX + u, c.originY + v];
}

export function pxToMm(c: KeonnCalibration, px: number, py: number): [number, number] | null {
  if (!isCalibrated(c)) return null;
  let u = px - c.originX;
  let v = py - c.originY;
  if (c.flipX) u = -u;
  if (c.flipY) v = -v;
  if (c.swapXY) [u, v] = [v, u];
  return [Math.round(u * c.mmPerPx), Math.round(v * c.mmPerPx)];
}

type Mode = "none" | "origin" | "measure1" | "measure2";

const HINTS: Record<Mode, string> = {
  none: "Hover the map to see Keonn positions. Use \"Pick on map\" or \"Measure on map\" to calibrate by clicking.",
  origin: "Click the map where Keonn's 0,0 point is.",
  measure1: "Click the first end of a wall or distance you know.",
  measure2: "Click the second end."
};

class FloorKeonnCalibration {
  private readonly img = document.getElementById("keonn_calibration_map_img") as HTMLImageElement;
  private readonly overlay = document.getElementById("keonn_calibration_overlay") as unknown as SVGSVGElement;
  private mode: Mode = "none";
  private measureA: [number, number] | null = null;
  private measureB: [number, number] | null = null;
  private saving = false;

  constructor() {
    this.bindEvents();
    this.render();
  }

  private bindEvents(): void {
    $(".keonn-calibration-input").on("input", (e) => this.sanitizeNumber(e.currentTarget as HTMLInputElement));
    $("#keonn_position_floor, #keonn_position_origin_x_px, #keonn_position_origin_y_px, #keonn_position_mm_per_px, #keonn_test_x, #keonn_test_y")
      .on("input", () => this.render());
    $("#keonn_position_swap_xy, #keonn_position_flip_x, #keonn_position_flip_y").on("change", () => this.render());

    $("#keonn_pick_origin_btn").on("click", () => {
      this.setMode(this.mode === "origin" ? "none" : "origin");
    });
    $("#keonn_measure_btn").on("click", () => {
      this.measureA = null;
      this.measureB = null;
      $("#keonn_measure_mm").val("");
      this.setMode(this.mode.startsWith("measure") ? "none" : "measure1");
    });
    $("#keonn_measure_apply").on("click", () => this.applyMeasure());
    $("#keonn-calibration").on("keydown", "input:not([name])", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      if (e.currentTarget.id === "keonn_measure_mm") this.applyMeasure();
    });
    $(document).on("keydown", (e) => {
      if (e.key === "Escape" && this.mode !== "none") this.setMode("none");
    });

    this.overlay.addEventListener("click", (e) => this.onMapClick(e));
    this.overlay.addEventListener("mousemove", (e) => this.onMapMove(e));
    this.overlay.addEventListener("mouseleave", () => $("#keonn_calibration_cursor").text("—"));
    this.img.addEventListener("load", () => this.render());
    $("#keonn_map_replace_btn").on("click", () => $("#keonn_map_input").trigger("click"));
    $("#keonn_map_input").on("change", (e) => this.previewMap(e.currentTarget as HTMLInputElement));
    $("form.keonn-calibration-layout").on("submit", (e) => {
      e.preventDefault();
      this.save(e.currentTarget as HTMLFormElement);
    });
    window.addEventListener("resize", () => this.render());
  }

  private sanitizeNumber(input: HTMLInputElement): void {
    const decimal = input.dataset.numeric === "decimal";
    const signed = input.dataset.signed === "true";
    const clean = (text: string): string => {
      let out = "";
      text
        .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
        .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/\u066B/g, ".")
        .split("")
        .forEach((ch) => {
          if (ch >= "0" && ch <= "9") out += ch;
          else if (ch === "." && decimal && out.indexOf(".") === -1) out += ch;
          else if (ch === "-" && signed && out === "") out += ch;
        });
      return out;
    };
    const value = clean(input.value);
    if (value === input.value) return;
    const caret = clean(input.value.slice(0, input.selectionStart ?? input.value.length)).length;
    input.value = value;
    input.setSelectionRange(caret, caret);
  }

  private previewMap(input: HTMLInputElement): void {
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.mode = "none";
      this.measureA = null;
      this.measureB = null;
      $(".keonn-calibration-thumb").attr("src", String(reader.result));
      this.img.src = String(reader.result);
      $("#keonn_map_replace_note").prop("hidden", false);
      this.setUpload("", 0, "");
    };
    reader.readAsDataURL(file);
  }

  private save(form: HTMLFormElement): void {
    if (this.saving) return;
    const fileInput = document.getElementById("keonn_map_input") as HTMLInputElement;
    const hasFile = !!(fileInput.files && fileInput.files.length);
    this.setSaving(true);
    if (hasFile) this.setUpload("", 0, "Uploading image… 0%");

    const xhr = new XMLHttpRequest();
    xhr.open("PATCH", form.action);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.upload.onprogress = (e) => {
      if (!hasFile || !e.lengthComputable) return;
      const percent = Math.round(e.loaded / e.total * 100);
      this.setUpload("", percent, percent < 100 ? `Uploading image… ${percent}%` : "Processing image…");
    };
    xhr.onload = () => {
      let body: { status?: string; message?: string; error?: string } = {};
      try { body = JSON.parse(xhr.responseText); } catch { body = {}; }
      if (xhr.status >= 200 && xhr.status < 300 && body.status === "ok") {
        if (hasFile) this.setUpload("done", 100, "Image uploaded.");
        fileInput.value = "";
        Toastr.success(body.message || "Keonn calibration saved.");
      } else {
        this.saveFailed(hasFile, body.error || "Could not save the Keonn calibration.");
      }
      this.setSaving(false);
    };
    xhr.onerror = () => {
      this.saveFailed(hasFile, "Connection failed. Nothing was saved.");
      this.setSaving(false);
    };
    xhr.send(new FormData(form));
  }

  private saveFailed(hasFile: boolean, message: string): void {
    if (hasFile) this.setUpload("failed", 100, `Upload failed: ${message}`);
    Toastr.error(message);
  }

  private setSaving(saving: boolean): void {
    this.saving = saving;
    $("#keonn_calibration_save").prop("disabled", saving).val(saving ? "Saving…" : "Save");
    $("#keonn_map_replace_btn").prop("disabled", saving);
  }

  private setUpload(state: "" | "done" | "failed", percent: number, text: string): void {
    $("#keonn_upload").prop("hidden", text === "").removeClass("done failed").addClass(state);
    $("#keonn_upload_bar").css("width", `${percent}%`);
    $("#keonn_upload_text").text(text);
  }

  private calibration(): KeonnCalibration {
    return {
      originX: this.num("#keonn_position_origin_x_px"),
      originY: this.num("#keonn_position_origin_y_px"),
      mmPerPx: this.num("#keonn_position_mm_per_px"),
      swapXY: $("#keonn_position_swap_xy").is(":checked"),
      flipX: $("#keonn_position_flip_x").is(":checked"),
      flipY: $("#keonn_position_flip_y").is(":checked")
    };
  }

  private num(selector: string): number {
    const value = String($(selector).val() ?? "").trim();
    return value === "" ? NaN : Number(value);
  }

  private naturalPoint(e: MouseEvent): [number, number] {
    const rect = this.img.getBoundingClientRect();
    return [
      Math.round((e.clientX - rect.left) * this.img.naturalWidth / rect.width),
      Math.round((e.clientY - rect.top) * this.img.naturalHeight / rect.height)
    ];
  }

  private setMode(mode: Mode): void {
    this.mode = mode;
    this.render();
  }

  private onMapClick(e: MouseEvent): void {
    if (this.mode === "none") return;
    const point = this.naturalPoint(e);
    if (this.mode === "origin") {
      $("#keonn_position_origin_x_px").val(point[0]);
      $("#keonn_position_origin_y_px").val(point[1]);
      this.mode = "none";
    } else if (this.mode === "measure1") {
      this.measureA = point;
      this.measureB = null;
      this.mode = "measure2";
    } else {
      this.measureB = point;
      this.mode = "none";
    }
    this.render();
  }

  private onMapMove(e: MouseEvent): void {
    const point = this.naturalPoint(e);
    const mm = pxToMm(this.calibration(), point[0], point[1]);
    $("#keonn_calibration_cursor").text(mm ? `x ${mm[0].toLocaleString("en-US")} · y ${mm[1].toLocaleString("en-US")} mm` : "—");
  }

  private measureLength(): number {
    if (!this.measureA || !this.measureB) return 0;
    return Math.hypot(this.measureB[0] - this.measureA[0], this.measureB[1] - this.measureA[1]);
  }

  private applyMeasure(): void {
    const real = this.num("#keonn_measure_mm");
    const px = this.measureLength();
    if (!(real > 0) || !(px > 0)) return;
    $("#keonn_position_mm_per_px").val(Math.round(real / px * 100) / 100);
    this.measureA = null;
    this.measureB = null;
    this.render();
  }

  private attrs(id: string, values: Record<string, number | string>): void {
    const el = document.getElementById(id);
    Object.keys(values).forEach((key) => el.setAttribute(key, String(values[key])));
  }

  private render(): void {
    const c = this.calibration();
    const calibrated = isCalibrated(c);
    const width = this.img.naturalWidth;
    const height = this.img.naturalHeight;
    const k = this.img.clientWidth > 0 ? width / this.img.clientWidth : 1;

    $("#keonn_calibration_status").text(calibrated ? "Calibrated" : "Not calibrated").toggleClass("calibrated", calibrated);
    $("#keonn_pick_origin_btn").toggleClass("active", this.mode === "origin");
    $("#keonn_measure_btn").toggleClass("active", this.mode.startsWith("measure"));
    $("#keonn_calibration_hint").text(HINTS[this.mode]).toggleClass("active", this.mode !== "none");
    this.overlay.classList.toggle("picking", this.mode !== "none");
    if (width > 0 && height > 0) this.overlay.setAttribute("viewBox", `0 0 ${width} ${height}`);

    const origin = mmToPx(c, 0, 0);
    $("#keonn_origin, #keonn_origin_label, #keonn_axis_x, #keonn_axis_x_label, #keonn_axis_y, #keonn_axis_y_label").toggle(!!origin);
    if (origin) {
      this.attrs("keonn_origin", { cx: origin[0], cy: origin[1], r: 7 * k, "stroke-width": 3 * k });
      this.attrs("keonn_origin_label", { x: origin[0] + 12 * k, y: origin[1] - 10 * k, "font-size": 12 * k });
      this.drawAxis(c, origin, 1000, 0, "keonn_axis_x", k);
      this.drawAxis(c, origin, 0, 1000, "keonn_axis_y", k);
    }

    const hasMeasure = !!(this.measureA && this.measureB);
    $("#keonn_measure_line").toggle(hasMeasure);
    $("#keonn_measure_box").prop("hidden", !hasMeasure);
    if (hasMeasure) {
      const [a, b] = [this.measureA, this.measureB];
      this.attrs("keonn_measure_segment", { x1: a[0], y1: a[1], x2: b[0], y2: b[1], "stroke-width": 2 * k, "stroke-dasharray": `${6 * k} ${4 * k}` });
      this.attrs("keonn_measure_a", { cx: a[0], cy: a[1], r: 5 * k });
      this.attrs("keonn_measure_b", { cx: b[0], cy: b[1], r: 5 * k });
      $("#keonn_measure_text").text(`Line is ${Math.round(this.measureLength())} px. Its real length in mm:`);
    }

    const dot = mmToPx(c, this.num("#keonn_test_x") || 0, this.num("#keonn_test_y") || 0);
    const inside = !!dot && dot[0] >= 0 && dot[0] <= width && dot[1] >= 0 && dot[1] <= height;
    $("#keonn_test_dot").toggle(inside);
    if (inside) {
      this.attrs("keonn_test_dot_outer", { cx: dot[0], cy: dot[1], r: 16 * k, "stroke-width": 2 * k });
      this.attrs("keonn_test_dot_center", { cx: dot[0], cy: dot[1], r: 3 * k });
    }
    let testText = "Fill in the calibration first.";
    if (calibrated) testText = inside ? `Lands at ${Math.round(dot[0])}, ${Math.round(dot[1])} px on the image.` : "Outside the image. Check 0,0, scale or axes.";
    $("#keonn_test_out").text(testText);
  }

  private drawAxis(c: KeonnCalibration, origin: [number, number], dx: number, dy: number, id: string, k: number): void {
    const p = mmToPx(c, dx, dy);
    const length = Math.hypot(p[0] - origin[0], p[1] - origin[1]) || 1;
    const end = [origin[0] + (p[0] - origin[0]) / length * 90 * k, origin[1] + (p[1] - origin[1]) / length * 90 * k];
    this.attrs(id, { x1: origin[0], y1: origin[1], x2: end[0], y2: end[1], "stroke-width": 3 * k });
    this.attrs(`${id}_label`, { x: end[0] + 6 * k, y: end[1] + 5 * k, "font-size": 14 * k });
  }
}

export function LoadPage() {
  InitPageStrict("floors keonn_calibration", () => {
    new FloorKeonnCalibration();
  });
}
